import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar,
  LinearProgress, Snackbar, Alert, Stack, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Divider, Tooltip, Menu, MenuItem, Tab, Tabs
} from '@mui/material';
import {
  Assignment, NotificationsActive, TrendingUp, QrCodeScanner, Business,
  Visibility, CheckCircle, Cancel, Edit, Delete, Add, Map as MapIcon,
  Download, FilterList, MoreVert, LocationOn, Phone, Email, Mic, MicOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useVoice } from '../contexts/VoiceContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

const AdminDashboard = () => {
  const queryClient = useQueryClient();
  const { sendAdminResponse, messages: socketMessages } = useSocket();
  const { isListening: isVoiceListening, startListening, stopListening, fullTranscript, resetTranscript, changeLanguage, interimTranscript, language, reinitialize } = useVoice();
  
  const handleSendMessage = () => {
    if (adminReply.trim()) {
      sendMessage.mutate(adminReply);
      if (isVoiceListening) stopListening();
      resetTranscript();
    }
  };
  useEffect(() => {
    if (fullTranscript) setAdminReply(fullTranscript);
  }, [fullTranscript]);
  const [activeTab, setActiveTab] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Dialog States
  const [mapOpen, setMapOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false); // For mobile/floating chat if needed
  const [adminReply, setAdminReply] = useState('');
  const [expDialogOpen, setExpDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editExp, setEditExp] = useState(null);
  const [chatInput, setChatInput] = useState('');

  // Queries
  const { data: tramitesData, isLoading: tramitesLoading } = useQuery('tramites', async () => {
    const res = await axios.get('/api/tramites');
    return res.data;
  });

  const { data: crotalesData, isLoading: crotalesLoading } = useQuery('crotales_admin', async () => {
    const res = await axios.get('/api/crotales_solicitudes');
    return res.data;
  });

  const { data: stats, isLoading: statsLoading } = useQuery(['dashboard', 'stats'], async () => {
    const res = await axios.get('/api/dashboard/stats');
    return res.data;
  });

  const { data: explotacionesData, isLoading: expLoading } = useQuery('explotaciones', async () => {
    const res = await axios.get('/api/explotaciones');
    return res.data;
  });

  const mensajes = socketMessages;

  const tramites = Array.isArray(tramitesData) ? tramitesData : [];
  const solicitudesCrotales = Array.isArray(crotalesData) ? crotalesData : [];
  const explotaciones = Array.isArray(explotacionesData) ? explotacionesData : [];

  // Mutations
  const updateTramite = useMutation(async ({ id, estado }) => {
    await axios.put(`/api/tramites/${id}`, { estado });
  }, { onSuccess: () => { queryClient.invalidateQueries('tramites'); showMessage('✅ Trámite actualizado'); } });

  const deleteTramite = useMutation(async (id) => {
    await axios.delete(`/api/tramites/${id}`);
  }, { onSuccess: () => { queryClient.invalidateQueries('tramites'); showMessage('🗑️ Trámite eliminado'); } });

  const updateCrotalStatus = useMutation(async ({ id, estado }) => {
    await axios.put(`/api/crotales_solicitudes/${id}`, { estado });
  }, { onSuccess: () => { queryClient.invalidateQueries('crotales_admin'); showMessage('✅ Solicitud de identificación procesada'); } });

  const upsertExplotacion = useMutation(async (data) => {
    if (data.id) await axios.put(`/api/explotaciones/${data.id}`, data);
    else await axios.post('/api/explotaciones', data);
  }, { onSuccess: () => { queryClient.invalidateQueries('explotaciones'); setExpDialogOpen(false); showMessage('✅ Registro de explotación sincronizado'); } });

  const deleteExplotacion = useMutation(async (id) => {
    await axios.delete(`/api/explotaciones/${id}`);
  }, { onSuccess: () => { queryClient.invalidateQueries('explotaciones'); showMessage('🗑️ Explotación retirada del registro'); } });

  const sendMessage = useMutation(async (text) => {
    await axios.post('/api/mensajes', { emisor: 'admin', texto: text });
  }, { 
    onSuccess: () => { 
      queryClient.invalidateQueries('mensajes'); 
      queryClient.invalidateQueries(['dashboard', 'stats']);
      setAdminReply(''); 
    } 
  });

  const executeAction = useMutation(async ({ type, data }) => {
    const res = await axios.post(`/api/acciones/${type}`, data);
    return res.data;
  }, { 
    onSuccess: (data) => { 
      queryClient.invalidateQueries(['dashboard', 'stats']); 
      showMessage(`🚀 Magia: ${data.crotal} registrado. Ref: ${data.referencia}`);
    } 
  });

  const showMessage = (msg) => { setSnackbarMessage(msg); setOpenSnackbar(true); };

  const handleMonthlyReport = () => {
    const content = `REPORTE MENSUAL BASERRI-ADITU\nFecha: ${new Date().toLocaleDateString()}\n\nTrámites Totales: ${tramites.length}\nExplotaciones Activas: ${explotaciones.length}\nCrotales Emitidos: ${solicitudesCrotales.filter(c => c.estado === 'entregado').length}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Reporte_Mensual_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    showMessage('📄 Reporte generado y descargado');
  };

  if (statsLoading || expLoading || tramitesLoading || crotalesLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><LinearProgress sx={{ borderRadius: 10, height: 10 }} /><Typography variant="h6" sx={{ mt: 2 }}>Sincronizando registros gubernamentales...</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Institutional Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'var(--navy-main)', letterSpacing: '-0.03em' }}>Consola de Gestión Administrativa</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 600 }}>Eusko Jaurlaritza • Control de Explotaciones y Trazabilidad</Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Voice Command & Diagnostic Status (Phase 21 Global) */}
          {isVoiceListening && (
            <Chip 
              label={language === 'es-ES' ? 'ESCUCHANDO...' : 'ENTZUTEN...'} 
              color="error" 
              size="small" 
              sx={{ fontWeight: 900, animation: 'pulse 1.5s infinite' }} 
            />
          )}
          <Tooltip title="Limpieza profunda (Corregir errores de conexión)">
            <IconButton onClick={reinitialize} size="small" sx={{ color: 'var(--slate-light)' }}>
              <TrendingUp sx={{ transform: 'rotate(90deg)', fontSize: '18px' }} />
            </IconButton>
          </Tooltip>
          <Chip 
            label={language?.split('-')[0].toUpperCase() || 'ES'} 
            onClick={() => changeLanguage(language === 'es-ES' ? 'eu' : 'es')}
            sx={{ fontWeight: 800, bgcolor: 'white', border: '1px solid var(--border-color)' }}
          />
          <Button variant="contained" startIcon={<Download />} onClick={handleMonthlyReport} sx={{ borderRadius: '12px', fontWeight: 800, bgcolor: 'var(--navy-main)', py: 1.5, px: 3 }}>
            Reporte Mensual
          </Button>
        </Stack>
      </Box>

      {/* KPI Ribbons */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Censo (Animales)', value: stats?.totalAnimales || '494', color: 'var(--emerald-main)', icon: <CheckCircle /> },
          { label: 'Stock Crotales', value: stats?.crotalesDisponibles || '14', color: 'var(--navy-main)', icon: <QrCodeScanner /> },
          { label: 'Trámites en Cola', value: stats?.movimientosPendientes || '0', color: 'var(--alert-gold)', icon: <Assignment /> },
          { label: 'Alertas Sanitarias', value: stats?.alertasActivas || '2', color: 'var(--alert-crimson)', icon: <NotificationsActive /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card className="premium-card shadow-sm">
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: 'var(--navy-main)' }}>{s.value}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{s.label}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 48, height: 48 }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: '1px solid var(--border-color)' }}>
        <Tab label="Cola de Validación" sx={{ fontWeight: 800 }} />
        <Tab label="Base de Explotaciones" sx={{ fontWeight: 800 }} />
        <Tab label="Monitor de Crotales" sx={{ fontWeight: 800 }} />
        <Tab label="Mensajería y Asistente" sx={{ fontWeight: 800, color: 'var(--emerald-main)' }} icon={<NotificationsActive sx={{ fontSize: 18 }} />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <Card className="premium-card animate-fadeIn">
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Ganadero / Solicitante</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Tipo de Trámite</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Fecha Solicitud</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones Institucionales</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tramites.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'var(--navy-main)', fontSize: '14px', fontWeight: 800 }}>{t.ganadero_nombre?.substring(0,2).toUpperCase()}</Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t.ganadero_nombre}</Typography>
                          <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>Exp: #4492-{t.explotacion_id}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={t.tipo} size="small" variant="outlined" sx={{ fontWeight: 800, borderColor: 'var(--navy-main)', color: 'var(--navy-main)' }} />
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{t.fecha_solicitud}</Typography></TableCell>
                    <TableCell>
                      <Chip label={t.estado} size="small" sx={{ 
                        fontWeight: 900, fontSize: '10px',
                        bgcolor: t.estado === 'pendiente' ? '#fffbeb' : '#f0f9ff',
                        color: t.estado === 'pendiente' ? 'var(--alert-gold)' : 'var(--navy-main)'
                      }} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Ver en Mapa"><IconButton size="small" onClick={() => { setSelectedItem(t); setMapOpen(true); }}><MapIcon color="primary" /></IconButton></Tooltip>
                        {t.estado === 'pendiente' && (
                          <>
                            <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => updateTramite.mutate({ id: t.id, estado: 'verificado' })} sx={{ borderRadius: '8px', fontWeight: 800 }}>Verificar</Button>
                            <Button size="small" variant="outlined" color="error" startIcon={<Cancel />} onClick={() => updateTramite.mutate({ id: t.id, estado: 'rechazado' })} sx={{ borderRadius: '8px', fontWeight: 800 }}>Rechazar</Button>
                          </>
                        )}
                        <IconButton size="small" onClick={() => deleteTramite.mutate(t.id)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {activeTab === 1 && (
        <Card className="premium-card animate-fadeIn">
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Registro Maestro de Explotaciones (REGA)</Typography>
            <Button variant="contained" className="luxury-button luxury-button-primary" startIcon={<Add />} onClick={() => { setEditExp({ nombre: '', codigo_rega: '', ubicacion: '', contacto: '' }); setExpDialogOpen(true); }}>
              Registrar Explotación
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Nombre / REGA</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Ubicación Geográfica</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Contacto</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {explotaciones.map((ex) => (
                  <TableRow key={ex.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{ex.nombre}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--navy-main)', fontWeight: 700 }}>{ex.codigo_rega}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOn sx={{ fontSize: '14px', color: 'var(--slate-light)' }} />
                        <Typography variant="body2">{ex.ubicacion}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{ex.contacto}</Typography>
                    </TableCell>
                    <TableCell><Chip label={ex.estado || 'activo'} size="small" color="success" sx={{ fontWeight: 800, fontSize: '10px' }} /></TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => { setEditExp(ex); setExpDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                      <IconButton color="error" onClick={() => deleteExplotacion.mutate(ex.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {activeTab === 2 && (
        <Card className="premium-card animate-fadeIn">
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Referencia / Ganadero</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Especie</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Cantidad</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Validación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {solicitudesCrotales.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>#SOL-{c.id}</Typography>
                      <Typography variant="caption">{c.fecha_solicitud}</Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{c.tipo_animal}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{c.cantidad} uds.</TableCell>
                    <TableCell align="right">
                      {c.estado === 'pendiente' ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="success" onClick={() => updateCrotalStatus.mutate({ id: c.id, estado: 'entregado' })}>Aprobar y Enviar</Button>
                        </Stack>
                      ) : (
                        <Chip label={c.estado} size="small" color="success" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3} className="animate-fadeIn">
          {/* Chat Window */}
          <Grid item xs={12} md={8}>
            <Card className="premium-card" sx={{ height: { xs: '500px', md: 'calc(100vh - 300px)' }, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, bgcolor: 'var(--slate-bg)', borderBottom: '1px solid var(--border-color)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Chat con Patxi (Jon García)</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {mensajes.map((m, i) => (
                  <Box key={i} sx={{ alignSelf: m.emisor === 'admin' ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                    <Paper sx={{ 
                      p: 2, 
                      bgcolor: m.emisor === 'admin' ? 'var(--navy-main)' : 'white',
                      color: m.emisor === 'admin' ? 'white' : 'inherit',
                      borderRadius: '16px',
                      border: m.emisor === 'admin' ? 'none' : '1px solid var(--border-color)'
                    }}>
                      <Typography variant="body2">{m.texto}</Typography>
                    </Paper>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: m.emisor === 'admin' ? 'right' : 'left' }}>
                      {new Date(m.fecha || m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider />
              {isVoiceListening && (
                <Box sx={{ p: 1, textAlign: 'center', bgcolor: '#fff5f5' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', display: 'block' }}>{interimTranscript || 'Hable ahora...'}</Typography>
                </Box>
              )}
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton 
                  onClick={() => {
                    if (isVoiceListening) {
                      stopListening();
                      setTimeout(() => { if (adminReply.trim()) handleSendMessage(); resetTranscript(); }, 500);
                    } else {
                      startListening();
                    }
                  }}
                  sx={{ bgcolor: isVoiceListening ? 'var(--alert-crimson)' : 'var(--emerald-main)', color: 'white', '&:hover': { bgcolor: isVoiceListening ? '#b91c1c' : 'var(--forest-main)' } }}
                >
                  {isVoiceListening ? <MicOff /> : <Mic />}
                </IconButton>
                <TextField 
                  fullWidth 
                  size="small" 
                  placeholder="Escribe o habla una respuesta..." 
                  value={adminReply}
                  onChange={(e) => setAdminReply(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <Button 
                  variant="contained" 
                  onClick={handleSendMessage} 
                  disabled={!adminReply.trim() && !isVoiceListening}
                  sx={{ borderRadius: '12px', fontWeight: 800, px: 3 }}
                >
                  Enviar
                </Button>
              </Box>
            </Card>
          </Grid>
          
          {/* Action Panel (Cognitive Assistant) */}
          <Grid item xs={12} md={4}>
            <Card className="premium-card" sx={{ height: { xs: 'auto', md: 'calc(100vh - 300px)' }, overflowY: 'auto' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Acciones Cognitivas</Typography>
                <Typography variant="caption" sx={{ color: 'var(--slate-light)', display: 'block', mb: 3 }}>Baserri-ADITU ha detectado intenciones en el chat. Ejecute para sincronizar.</Typography>
                
                <Stack spacing={2}>
                  <Card sx={{ p: 2, border: '1px solid var(--emerald-main)', bgcolor: 'rgba(16, 185, 129, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--emerald-main)' }}>🐣 Registro de Nacimiento</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>Madre: Pinta (402) | Raza: Terreña</Typography>
                    <Button fullWidth variant="contained" color="success" onClick={() => executeAction.mutate({ type: 'nacimiento', data: { madre_alias: 'Pinta', sexo: 'macho' } })}>
                      Confirmar y Registrar
                    </Button>
                  </Card>

                  <Card sx={{ p: 2, border: '1px solid var(--alert-crimson)', bgcolor: 'rgba(239, 68, 68, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--alert-crimson)' }}>🚫 Bloqueo de Guía</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>Animal: Txuri | Motivo: Restricción Sanitaria SE-001</Typography>
                    <Button fullWidth variant="outlined" color="error" onClick={() => showMessage('⚠️ Acción Bloqueada: Motivos de Sanidad Animal')}>
                      Informar Restricción
                    </Button>
                  </Card>

                  {mensajes.some(m => m.intent === 'vacunacion') && (
                    <Card sx={{ p: 2, border: '1px solid var(--navy-main)', bgcolor: 'rgba(15, 23, 42, 0.05)', animation: 'pulse 2s infinite' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>💉 Crear Alerta Vacunación</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>Protocolo primavera detectado para lote A.</Typography>
                      <Button fullWidth variant="contained" sx={{ bgcolor: 'var(--navy-main)' }} onClick={() => executeAction.mutate({ type: 'vacunacion', data: { lote: 'A', enfermedad: 'Lengua Azul' } })}>
                        Registrar y Notificar
                      </Button>
                    </Card>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Map Modal */}
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Ubicación del Trámite / Explotación</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
             {/* Institutional Map Placeholder */}
             <Box sx={{ textAlign: 'center' }}>
               <LocationOn sx={{ fontSize: 60, color: 'var(--alert-crimson)', mb: 1 }} />
               <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedItem?.ganadero_nombre}</Typography>
               <Typography variant="body2">Coordenadas: 43.2630° N, 2.9350° W (Bizkaia)</Typography>
             </Box>
             <Box sx={{ position: 'absolute', top: 10, right: 10, p: 1, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '8px', border: '1px solid #ddd' }}>
               <Typography variant="caption" sx={{ fontWeight: 800 }}>VISUALIZACIÓN SATÉLITE</Typography>
             </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Detalles Técnicos</Typography>
            <Typography variant="body2" sx={{ color: 'var(--slate-light)' }}>{selectedItem?.detalles || 'Solicitud estándar de gestión ganadera.'}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}><Button onClick={() => setMapOpen(false)}>Cerrar Mapa</Button></DialogActions>
      </Dialog>

      {/* Explotacion Dialog */}
      <Dialog open={expDialogOpen} onClose={() => setExpDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>{editExp?.id ? 'Editar Explotación' : 'Registrar Nueva Explotación'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nombre de Explotación" value={editExp?.nombre} onChange={(e) => setEditExp({...editExp, nombre: e.target.value})} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código REGA" placeholder="ESxxxxxxxxxxxx" value={editExp?.codigo_rega} onChange={(e) => setEditExp({...editExp, codigo_rega: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Ubicación Detallada" value={editExp?.ubicacion} onChange={(e) => setEditExp({...editExp, ubicacion: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Información de Contacto" value={editExp?.contacto} onChange={(e) => setEditExp({...editExp, contacto: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setExpDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => upsertExplotacion.mutate(editExp)}>Guardar Registro</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px', fontWeight: 700 }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
